import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { queryClient } from '../../../config/queryClient'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { InvoiceDocumentType } from '../enums/invoiceDocumentType'
import { deleteSalesDocumentDraft } from '../query-api'
import { getDocumentDraftDeletionNotification } from '../utils/getDocumentDraftDeletionNotification'
import { isDocumentTypeQuote } from '../utils/isDocumentTypeQuote'

interface UseDeleteSalesDocumentDraftOptions {
  onSuccess?: () => void
}

interface DeleteDraftOptions {
  approvedEstimateId?: string
  documentId: number
  documentType: InvoiceDocumentType
}

export const useDeleteSalesDocumentDraft = ({ onSuccess }: UseDeleteSalesDocumentDraftOptions = {}) => {
  const { t } = useTranslation()

  const { mutate: deleteDraftFn, isLoading: isProcessing } = useMutation(deleteSalesDocumentDraft)

  const deleteDraft = useCallback(
    ({ approvedEstimateId, documentId, documentType }: DeleteDraftOptions) => {
      return deleteDraftFn(
        { documentId, documentType },
        {
          onSuccess: () => {
            if (isDocumentTypeQuote(documentType)) {
              queryClient.invalidateQueries(QueryKeys.Quotes)
              queryClient.invalidateQueries(QueryKeys.QuotesSummary)
              queryClient.invalidateQueries(QueryKeys.ClientQuotes)
            } else {
              queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
              queryClient.invalidateQueries(QueryKeys.InvoicesSummary)
              queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
              if (approvedEstimateId) {
                queryClient.invalidateQueries([QueryKeys.Quote, approvedEstimateId])
              }
            }
            onSuccess?.()
            notify({
              id: NotificationKeys.ExternalInvoiceDraftDeleted,
              message: getDocumentDraftDeletionNotification(documentType, t),
              variant: 'success',
            })
          },
          onError: () => {
            notify({
              id: NotificationKeys.ExternalInvoiceDraftDeleted,
              message: t('error_saving'),
              variant: 'error',
            })
          },
        },
      )
    },
    [deleteDraftFn, onSuccess, t],
  )

  return { deleteSalesDocumentDraft: deleteDraft, isProcessing }
}
